import {
    Component,
    computed,
    inject,
    input,
    output,
    signal,
    ViewEncapsulation,
    ChangeDetectionStrategy,
} from "@angular/core";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25IconComponent } from "../../modules/s25-icon/s25.icon.component";
import { S25Icons } from "../../modules/s25-icon/s25.icon.const";
import { Item } from "../../pojo/Item";
import { FavoriteService } from "../../services/favorite.service";
import { LoginStore } from "../../stores/login.store";
import { S25Util } from "../../util/s25-util";
import { ItemService } from "../../util/items/item.service";
import { StoredItem } from "../../util/items/item.store.internal";

@TypeManagerDecorator("s25-ng-favorite-button")
@Component({
    selector: "s25-ng-favorite-button",
    template: `
        @if (isLoggedIn()) {
            @if (display() === "star") {
                <s25-ng-icon
                    aria-hidden="true"
                    class="favorite-button__star-icon"
                    [class.favorite-button__star-icon--off]="!isFavorite()"
                    [class.favorite-button__star-icon--on]="isFavorite()"
                    [autoDarkMode]="true"
                    [ariaLabel]="message()"
                    [type]="isFavorite() ? Icons.starOn : Icons.starOff"
                />
            } @else if (display() === "bookmark") {
                @if (!isFavorite()) {
                    <s25-ng-icon [type]="'bookmarkOutline'" [ariaLabel]="message()" />
                } @else {
                    <s25-ng-icon [type]="'bookmarkFilled'" [ariaLabel]="message()" />
                }
            }
        } @else {}
    `,
    host: {
        role: "button",
        tabindex: "0",
        "[class.favorite-button]": "true",
        "[class.favorite-button--pending]": "pending() !== undefined",
        "[attr.data-display]": "display()",
        "[attr.aria-label]": "message()",
        "(click)": "toggle()",
        "(keyup.enter)": "toggle()",
        "(keyup.space)": "toggle()",
        "(keydown.space)": "$event.preventDefault()",
    },
    encapsulation: ViewEncapsulation.Emulated,
    styleUrls: ["./s25.favorite.button.component.scss"],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [S25IconComponent],
})
export class S25FavoriteButtonComponent {
    public readonly itemId = input<number>();
    public readonly itemTypeId = input<Item.Id>();
    public readonly display = input<"star" | "bookmark">("star");
    public readonly favoriteChange = output<boolean>();

    private readonly itemService = inject(ItemService);
    private readonly loginStore = inject(LoginStore);
    public readonly isLoggedIn = this.loginStore.loggedIn;

    public readonly isFavorite = computed((): boolean | undefined => {
        const itemId = this.itemId();
        const itemTypeId = this.itemTypeId();
        if (!this.isLoggedIn() || !itemId || !itemTypeId) return undefined;
        if (S25Util.isDefined(this.pending())) {
            return this.pending();
        }
        const item = this.itemService.get({ itemId, itemTypeId }, { fetch: { favorite: true } });
        if (!StoredItem.hasProps(item, { favorite: true })) {
            return undefined;
        }
        return item.favorite === "T" || item.favorite === true;
    });
    public readonly message = computed(() => {
        if (this.display() === "star") {
            if (this.pending() !== undefined) {
                return this.pending() ? "Adding to Favorites..." : "Removing from Favorites...";
            } else {
                return this.isFavorite() ? "Remove from Favorites" : "Add to Favorites";
            }
        }
        if (this.display() === "bookmark") {
            if (this.pending() !== undefined) {
                return "Updating...";
            } else {
                return this.isFavorite() ? "Unfollow" : "Follow";
            }
        }
    });

    public readonly pending = signal<boolean | undefined>(undefined);

    protected readonly Icons = S25Icons.names;

    public async toggle() {
        if (this.pending() === undefined && S25Util.isDefined(this.isFavorite())) {
            const itemId = this.itemId()!;
            const itemTypeId = this.itemTypeId()!;
            const wasFavorite = this.isFavorite()!;
            const newValue = !wasFavorite;
            this.pending.set(newValue);
            try {
                await FavoriteService.toggle({
                    isFav: wasFavorite,
                    itemId,
                    itemTypeId,
                });
                this.favoriteChange.emit(newValue);
                this.itemService.storeItem(
                    { itemId, itemTypeId, favorite: newValue },
                    {
                        favorite: true,
                    },
                );
            } finally {
                this.pending.set(undefined);
            }
        }
    }
}
